/**
 * Does the Studio's draft differ from the report as stored?
 *
 * The draft's filters always carry a `report` design (the Studio builds one
 * whether or not the view was ever designed), and a stored view that never was
 * has none — so comparing the two as raw JSON said "unsaved changes" on a view
 * nobody had touched, and "Discard the unsaved changes?" fired on a click that
 * had nothing to discard. It also compared key ORDER: the server returns the
 * same filters in whatever order it stored them.
 *
 * Both sides are normalised first — the default design where none is stored,
 * the default filters where a key is absent, filters that constrain nothing
 * dropped — and compared as sorted-key JSON.
 */
import { isActiveColumnFilter } from './reporting'
import { EMPTY_FILTERS, normalizeReportConfig, type ColumnFilterValue, type ColumnId } from './types'

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, item]) => [key, sortKeys(item)]),
    )
  }
  return value
}

/** JSON with keys in a fixed order, so equal values read as equal text. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeys(value))
}

/** A view's filters as comparison text: defaults filled in, the design normalised. */
export function normalizedFiltersKey(filters: unknown): string {
  const { report, ...rest } = (filters && typeof filters === 'object' ? filters : {}) as Record<string, unknown>
  const config = normalizeReportConfig(report as Parameters<typeof normalizeReportConfig>[0])
  const columnFilters = Object.fromEntries(
    Object.entries(config.columnFilters).filter(([, filter]) =>
      isActiveColumnFilter(filter as ColumnFilterValue | undefined),
    ),
  ) as Partial<Record<ColumnId, ColumnFilterValue>>
  return stableStringify({
    ...EMPTY_FILTERS,
    ...rest,
    // The data scope's absent value is 'active' (a view saved before dead deals
    // could be reported on); it is the same report as one that says so.
    dealStatus: rest.dealStatus ?? 'active',
    report: { ...config, columnFilters },
  })
}

export function isDesignDirty(
  draft: { columns: readonly string[]; filters: unknown },
  saved: { columns: readonly string[]; filters: unknown },
): boolean {
  return (
    stableStringify(draft.columns) !== stableStringify(saved.columns) ||
    normalizedFiltersKey(draft.filters) !== normalizedFiltersKey(saved.filters)
  )
}
